export type DecimalInput = Decimal | string | number | bigint;
/** Python Decimal's localcontext(prec=100, rounding=ROUND_HALF_EVEN). */
export class Decimal {
  private units: bigint;
  private scale: number;
  constructor(value: DecimalInput) {
    if (value instanceof Decimal) {
      this.units = value.units;
      this.scale = value.scale;
      return;
    }
    const m = /^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(String(value));
    if (!m) throw new Error('Decimal inválido');
    const exponent = Number(m[4] ?? 0);
    if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 10000)
      throw new Error('Exponente inválido');
    this.units = BigInt(m[2] + (m[3] ?? '')) * (m[1] === '-' ? -1n : 1n);
    this.scale = (m[3]?.length ?? 0) - exponent;
    this.normalize();
  }
  private normalize() {
    if (this.units === 0n) {
      this.scale = 0;
      return;
    }
    while (this.units % 10n === 0n) {
      this.units /= 10n;
      this.scale--;
    }
  }
  private static rounded(n: bigint, d: bigint, halfUp = false) {
    const sign = n < 0n ? -1n : 1n,
      a = n < 0n ? -n : n,
      q = a / d,
      r = a % d;
    return sign * (q + (r * 2n > d || (r * 2n === d && (halfUp || q % 2n !== 0n)) ? 1n : 0n));
  }
  private static make(units: bigint, scale: number, precision = true) {
    const value = new Decimal(0);
    value.units = units;
    value.scale = scale;
    const digits = (units < 0n ? -units : units).toString().length;
    if (precision && digits > 100) {
      const drop = digits - 100;
      value.units = Decimal.rounded(units, 10n ** BigInt(drop));
      value.scale -= drop;
    }
    value.normalize();
    return value;
  }
  add(other: DecimalInput): Decimal {
    const b = new Decimal(other),
      scale = Math.max(this.scale, b.scale);
    return Decimal.make(
      this.units * 10n ** BigInt(scale - this.scale) + b.units * 10n ** BigInt(scale - b.scale),
      scale,
    );
  }
  sub(other: DecimalInput): Decimal {
    const b = new Decimal(other);
    return this.add(Decimal.make(-b.units, b.scale, false));
  }
  mul(other: DecimalInput): Decimal {
    const b = new Decimal(other);
    return Decimal.make(this.units * b.units, this.scale + b.scale);
  }
  div(other: DecimalInput): Decimal {
    const b = new Decimal(other);
    if (b.units === 0n) throw new Error('División por cero');
    if (this.units === 0n) return new Decimal(0);
    const a = this.units < 0n ? -this.units : this.units,
      d = b.units < 0n ? -b.units : b.units;
    let exponent = a.toString().length - d.toString().length;
    if (exponent >= 0 ? a < d * 10n ** BigInt(exponent) : a * 10n ** BigInt(-exponent) < d)
      exponent--;
    const shift = 99 - exponent,
      numerator = shift >= 0 ? a * 10n ** BigInt(shift) : a;
    const denominator = shift >= 0 ? d : d * 10n ** BigInt(-shift);
    const sign = this.units < 0n !== b.units < 0n ? -1n : 1n;
    return Decimal.make(
      sign * Decimal.rounded(numerator, denominator),
      shift + this.scale - b.scale,
    );
  }
  compare(other: DecimalInput): number {
    const difference = this.sub(other).units;
    return difference < 0n ? -1 : difference > 0n ? 1 : 0;
  }
  round(places: number, mode: 'half_even' | 'half_up' = 'half_even'): Decimal {
    return this.scale <= places
      ? new Decimal(this)
      : Decimal.make(
          Decimal.rounded(this.units, 10n ** BigInt(this.scale - places), mode === 'half_up'),
          places,
          false,
        );
  }
  toNumber(): number {
    return Number(this.toString());
  }
  toString(): string {
    const sign = this.units < 0n ? '-' : '',
      digits = (this.units < 0n ? -this.units : this.units).toString();
    if (this.scale <= 0) return sign + digits + '0'.repeat(-this.scale);
    const padded = digits.padStart(this.scale + 1, '0');
    return sign + padded.slice(0, -this.scale) + '.' + padded.slice(-this.scale);
  }
}
